import { makelaar } from "../data/property";
import { Phone, Mail, Globe, Star, MapPin, Clock, Award } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import Reveal from "./Reveal";

export default function Makelaar() {
  return (
    <section
      id="makelaar"
      data-testid="makelaar-section"
      className="bg-brand-bg py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20">
        <Reveal className="max-w-3xl">
          <p className="overline">De makelaar</p>
          <h2 className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl text-balance">
            Persoonlijk advies van een{" "}
            <span className="italic">lokale expert</span>.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-0">
          {/* Portrait */}
          <Reveal className="md:col-span-5">
            <div className="relative aspect-[4/5] flex items-end justify-center">
              <img
                src={makelaar.portrait}
                alt={makelaar.name}
                className="h-full w-full object-contain object-bottom"
                loading="lazy"
              />
            </div>
            <div className="mt-4 flex items-center gap-2 text-brand-ink">
              <Star size={16} className="fill-brand-gold text-brand-gold" />
              <span data-testid="makelaar-rating" className="font-serif text-xl">
                {makelaar.rating}
              </span>
              <span className="text-sm text-brand-muted">
                · gemiddelde van {makelaar.reviewCount} beoordelingen
              </span>
            </div>
          </Reveal>

          {/* Info & CTAs */}
          <Reveal className="border-brand-border bg-brand-surface p-8 md:col-span-7 md:p-12 lg:p-16" delay={0.15}>
            <h3 data-testid="makelaar-name" className="font-serif text-3xl text-brand-ink sm:text-4xl">
              {makelaar.name}
            </h3>
            <p className="mt-2 text-brand-muted">{makelaar.title}</p>

            <div className="mt-8 hairline" />

            <p className="mt-8 text-lg leading-relaxed text-brand-muted italic font-serif">
              "Deskundig, persoonlijk en altijd goed bereikbaar. Carole staat voor u klaar, schakelt snel en overziet het hele proces."
            </p>
            <p className="mt-2 text-xs text-brand-muted">— Funda-beoordeling</p>

            {/* Contact details */}
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3 text-sm">
                <MapPin size={16} className="mt-1 text-brand-moss" />
                <div>
                  <p className="text-brand-ink">{makelaar.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Clock size={16} className="mt-1 text-brand-moss" />
                <div>
                  <p className="text-brand-ink">{makelaar.hours}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Award size={16} className="mt-1 text-brand-moss" />
                <div>
                  <p className="text-brand-ink">{makelaar.certificates.join(" · ")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Globe size={16} className="mt-1 text-brand-moss" />
                <div>
                  <p className="text-brand-ink">{makelaar.languages.join(" · ")}</p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a
                data-testid="makelaar-call-btn"
                href={`tel:${makelaar.phoneClean}`}
                onClick={() => trackEvent("cta_click", { location: "makelaar", action: "call", value: makelaar.phone })}
                className="inline-flex items-center justify-center gap-2 bg-brand-moss px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-brand-mossDark"
              >
                <Phone size={16} />
                Bel {makelaar.phone}
              </a>
              <a
                data-testid="makelaar-email-btn"
                href={`mailto:${makelaar.email}?subject=Interesse Kempenaar 29, Wijk bij Duurstede`}
                onClick={() => trackEvent("cta_click", { location: "makelaar", action: "email" })}
                className="inline-flex items-center justify-center gap-2 border border-brand-moss px-8 py-4 text-sm font-medium text-brand-moss transition-colors hover:bg-brand-moss hover:text-white"
              >
                <Mail size={16} />
                Stuur een e-mail
              </a>
              <a
                data-testid="makelaar-website-btn"
                href={makelaar.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("outbound_click", { location: "makelaar", url: makelaar.website })}
                className="inline-flex items-center justify-center gap-2 px-4 py-4 text-sm font-medium text-brand-ink transition-colors hover:text-brand-moss"
              >
                <Globe size={16} />
                Website
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
